import { MoonIcon } from "@phosphor-icons/react";
import { useIntl } from "react-intl";

import { Avatar } from "@/components/Avatar/Avatar";
import {
  cabinClassLabels,
  isCabinClass,
} from "@/features/flights/utils/cabinClasses";
import type {
  FindFlightOffersAirlineV3,
  FindFlightOffersFlightSegmentV3,
} from "@/types/codegen";
import { parseDuration } from "@/utils/duration";

const AirlineNameAndLogo = ({
  airline,
}: {
  airline: FindFlightOffersAirlineV3;
}) => {
  const intl = useIntl();

  return (
    <div className="flex items-center gap-1">
      <span>{airline.name}</span>
      <Avatar
        alt={intl.formatMessage(
          { id: "flights.airlineLogoAlt", defaultMessage: "{airline} logo" },
          { airline: airline.name || airline.code },
        )}
        size="small"
        src={airline.logoUrl}
      />
    </div>
  );
};

type Props = {
  segment: FindFlightOffersFlightSegmentV3;
  cabinClasses: string[];
  isOvernight: boolean;
};

export const SegmentDetails = ({
  segment,
  cabinClasses,
  isOvernight,
}: Props) => {
  const intl = useIntl();

  const areCarriersDiffer =
    segment.operatingCarrier.airline.code !==
    segment.marketingCarrier.airline.code;

  // We assume that all travellers has same cabin class
  const cabinClass = cabinClasses[0];

  const segmentDuration = segment.duration
    ? intl.formatMessage(
        { id: "durationValue", defaultMessage: "{hours}h {minutes}m" },
        parseDuration(segment.duration),
      )
    : null;

  return (
    <div className="flex flex-col gap-1 text-sm text-text-primary">
      <AirlineNameAndLogo airline={segment.marketingCarrier.airline} />
      {areCarriersDiffer ? (
        <div className="flex flex-col gap-0">
          <span>
            {intl.formatMessage({
              id: "flights.operatedBy",
              defaultMessage: "Operated by",
            })}
            :
          </span>
          <AirlineNameAndLogo airline={segment.operatingCarrier.airline} />
        </div>
      ) : null}
      <span>
        {segment.marketingCarrier.airline.code}-
        {segment.marketingCarrier.flightNumber}
      </span>
      <span>
        {segment.equipment.name ? `${segment.equipment.name} ` : null}
        {segment.equipment.code}
      </span>
      <span>
        {isCabinClass(cabinClass)
          ? intl.formatMessage(cabinClassLabels[cabinClass])
          : cabinClass}
      </span>
      <span>{segmentDuration}</span>
      {isOvernight ? (
        <div className="flex items-center gap-0.5 text-semantic-error-strong">
          <span>
            {intl.formatMessage({
              id: "flights.overnightFlight",
              defaultMessage: "Overnight flight",
            })}
          </span>
          <MoonIcon size={16} weight="bold" />
        </div>
      ) : null}
    </div>
  );
};
